// WGSL for GPU pigment shading (rendering/PigmentShading.shadeCell) and
// surface lighting (rendering/Lighting.lightCell). Shading writes three
// storage textures for dirty tiles only; lighting is a full-screen render pass
// that reads them. Pigment albedo is never modified by lighting.

import { BAND_COUNT, BAND_TO_LINEAR_RGB } from '../pigments/ColourConversion.js';
import { PIGMENT_SLOTS, TILE_SIZE } from '../core/PaintState.js';
import { SAUNDERSON_K1, SAUNDERSON_K2 } from '../pigments/SpectralMixing.js';
import { RELIEF } from '../rendering/PigmentShading.js';
import { DEFAULT_PAPER_PARAMS } from '../physics/PaperAbsorption.js';
import { FIELD, PIGMENT_RECORD } from './simShader.js';

const f = (v) => {
  const s = Number(v).toString();
  return /[.eE]/.test(s) ? s : `${s}.0`;
};

export function buildShadeShader() {
  const F = FIELD;
  const m = BAND_TO_LINEAR_RGB;
  const row = (c) => `array<f32, ${BAND_COUNT}>(${Array.from(m[c]).map(f).join(', ')})`;
  return /* wgsl */ `
struct Params {
  W: u32, H: u32, tilesX: u32, tilesY: u32,
  dt: f32, tiltX: f32, tiltY: f32, evapScale: f32,
  dryingScale: f32, slotsMask: u32, surfaceSheen: f32, granulationScale: f32,
};
@group(0) @binding(0) var<uniform> prm: Params;
@group(0) @binding(1) var<storage, read> S: array<f32>;
@group(0) @binding(2) var<storage, read> P: array<f32>;
@group(0) @binding(3) var<storage, read> R: array<f32>;
@group(0) @binding(4) var<storage, read> surf: array<f32>;
@group(0) @binding(5) var<storage, read_write> T: array<atomic<u32>>;
@group(0) @binding(6) var<uniform> pig: array<vec4<f32>, ${(PIGMENT_SLOTS * PIGMENT_RECORD) / 4}>;
@group(0) @binding(7) var albedoTex: texture_storage_2d<rgba8unorm, write>;
@group(0) @binding(8) var elevationTex: texture_storage_2d<r32float, write>;
@group(0) @binding(9) var materialTex: texture_storage_2d<rgba8unorm, write>;

const BANDS: u32 = ${BAND_COUNT}u;
const SLOTS: u32 = ${PIGMENT_SLOTS}u;
const TILE: u32 = ${TILE_SIZE}u;
const MR = ${row(0)};
const MG = ${row(1)};
const MB = ${row(2)};

fn N() -> u32 { return prm.W * prm.H; }
fn nt() -> u32 { return prm.tilesX * prm.tilesY; }
fn sf(field: u32, i: u32) -> f32 { return S[field * N() + i]; }
fn pi(layer: u32, k: u32, i: u32) -> u32 { return (layer * SLOTS + k) * N() + i; }
fn pigK(k: u32, band: u32) -> f32 { let o = k * ${PIGMENT_RECORD}u + band; return pig[o / 4u][o % 4u]; }
fn pigS(k: u32, band: u32) -> f32 { let o = k * ${PIGMENT_RECORD}u + 12u + band; return pig[o / 4u][o % 4u]; }
fn slotOn(k: u32) -> bool { return (prm.slotsMask & (1u << k)) != 0u; }

fn qCothQ(q: f32) -> f32 {
  if (q < 1e-3) { return 1.0 + q * q / 3.0; }
  if (q > 20.0) { return q; }
  return q / tanh(q);
}
fn kmLayer(KX: f32, SX: f32, rg: f32) -> f32 {
  let k = max(KX, 0.0);
  let s = max(SX, 0.0);
  if (k < 1e-9 && s < 1e-9) { return rg; }
  if (s < 1e-7) { return rg * exp(-2.0 * k); }
  let q = sqrt(k * k + 2.0 * k * s);
  let qc = qCothQ(q);
  return clamp((s - rg * (s + k - qc)) / (s + k - rg * s + qc), 0.0, 1.0);
}

fn composite(spec: ptr<function, array<f32, ${BAND_COUNT}>>, i: u32, layer: u32) {
  var present = false;
  for (var k = 0u; k < SLOTS; k++) { if (slotOn(k) && P[pi(layer, k, i)] > 1e-7) { present = true; } }
  if (!present) { return; }
  for (var band = 0u; band < BANDS; band++) {
    var KX = 0.0;
    var SX = 0.0;
    for (var k = 0u; k < SLOTS; k++) {
      if (!slotOn(k)) { continue; }
      let a = P[pi(layer, k, i)];
      if (a <= 1e-7) { continue; }
      KX += a * pigK(k, band);
      SX += a * pigS(k, band);
    }
    (*spec)[band] = kmLayer(KX, SX, (*spec)[band]);
  }
}

fn toSrgb(v: f32) -> f32 {
  let c = clamp(v, 0.0, 1.0);
  return select(1.055 * pow(c, 1.0 / 2.4) - 0.055, 12.92 * c, c <= 0.0031308);
}

// One thread per cell of each dirty tile.
@compute @workgroup_size(8, 8)
fn shadeTiles(@builtin(global_invocation_id) gid: vec3<u32>) {
  if (gid.x >= prm.W || gid.y >= prm.H) { return; }
  let t = (gid.y / TILE) * prm.tilesX + gid.x / TILE;
  if (atomicLoad(&T[2u * nt() + t]) != 1u) { return; }
  let i = gid.y * prm.W + gid.x;
  var spec: array<f32, ${BAND_COUNT}>;
  for (var b = 0u; b < BANDS; b++) { spec[b] = R[i * BANDS + b]; }
  composite(&spec, i, 1u);
  composite(&spec, i, 0u);
  let hasBody = sf(${F.bodyVol}u, i) > 1e-4;
  if (hasBody) { composite(&spec, i, 2u); }
  let h = sf(${F.water}u, i);
  let wet = min(1.0, sf(${F.paperSat}u, i) / ${f(DEFAULT_PAPER_PARAMS.capacity)} + h * 6.0);
  let exponent = 1.0 + 0.45 * wet;
  var mr = MR;
  var mg = MG;
  var mb = MB;
  var rgb = vec3<f32>(0.0);
  for (var b = 0u; b < BANDS; b++) {
    var r = spec[b];
    if (wet > 0.0) { r = pow(r, exponent); }
    let body = ((1.0 - ${f(SAUNDERSON_K1)}) * (1.0 - ${f(SAUNDERSON_K2)}) * r) / (1.0 - ${f(SAUNDERSON_K2)} * r);
    rgb += vec3<f32>(mr[b], mg[b], mb[b]) * body;
  }
  let bodyRelief = select(0.0, sf(${F.bodyVol}u, i), hasBody);
  let elevation = ${f(RELIEF.dried)} * sf(${F.dryHeight}u, i) + ${f(RELIEF.body)} * bodyRelief + ${f(RELIEF.water)} * h;
  var gloss = prm.surfaceSheen;
  if (sf(${F.dryHeight}u, i) > 0.01) { gloss = 0.35; }
  if (hasBody) { gloss = max(gloss, sf(${F.bodyGloss}u, i) * (0.4 + 0.6 * sf(${F.bodyOpen}u, i))); }
  if (h > 0.004) { gloss = max(gloss, min(0.92, 0.5 + h * 8.0)); }
  let wetness = max(wet, select(0.0, sf(${F.bodyOpen}u, i), hasBody));
  // Hiding power: how strongly the paint's scattering covers what lies
  // beneath (thick white gouache hides cream paper even though its colour
  // matches the white substrate the layer is computed on).
  var scatter = 0.0;
  for (var k = 0u; k < SLOTS; k++) {
    if (!slotOn(k)) { continue; }
    var amount = P[pi(0u, k, i)] + P[pi(1u, k, i)];
    if (hasBody) { amount += P[pi(2u, k, i)]; }
    scatter += amount * pigS(k, BANDS / 2u);
  }
  let hiding = 1.0 - exp(-0.8 * scatter);
  let p = vec2<i32>(i32(gid.x), i32(gid.y));
  textureStore(albedoTex, p, vec4<f32>(toSrgb(rgb.x), toSrgb(rgb.y), toSrgb(rgb.z), 1.0));
  textureStore(elevationTex, p, vec4<f32>(elevation, 0.0, 0.0, 1.0));
  textureStore(materialTex, p, vec4<f32>(gloss, wetness, hiding, 1.0));
}

@compute @workgroup_size(64)
fn clearDirty(@builtin(global_invocation_id) gid: vec3<u32>) {
  if (gid.x < nt()) { atomicStore(&T[2u * nt() + gid.x], 0u); }
}
`;
}

// Light pass. Modes:
//   0 opaque     paint over the paper colour (development Lab)
//   1 layer      premultiplied paint for compositing over the document's paper:
//                alpha is the smallest coverage that reproduces the paint over a
//                white substrate, so neutral glazes act multiplicatively on
//                whatever lies beneath (paper or lower layers)
//   2 paper      the document surface alone (paper colour + relief)
//   3 coverage   the layer alpha of mode 1, as an opaque grey image
export const LIGHT_UNIFORM_BYTES = 112;

export function buildLightShader() {
  return /* wgsl */ `
struct Light {
  dir: vec3<f32>, intensity: f32,
  colour: vec3<f32>, ambient: f32,
  specular: f32, roughness: f32, softness: f32, depth: f32,
  exposure: f32, view: u32, maxElevation: f32, mode: u32,
  size: vec2<f32>, paperRelief: f32, paperSheen: f32,
  paperAlbedo: vec3<f32>, pad0: f32,
  substrateAlbedo: vec3<f32>, pad1: f32,
};
@group(0) @binding(0) var<uniform> light: Light;
@group(0) @binding(1) var albedoTex: texture_2d<f32>;
@group(0) @binding(2) var elevationTex: texture_2d<f32>;
@group(0) @binding(3) var materialTex: texture_2d<f32>;
@group(0) @binding(4) var linearSampler: sampler;
@group(0) @binding(5) var paperTex: texture_2d<f32>;

struct VOut { @builtin(position) pos: vec4<f32>, @location(0) uv: vec2<f32> };

@vertex
fn vs(@builtin(vertex_index) v: u32) -> VOut {
  var corners = array<vec2<f32>, 3>(vec2<f32>(-1.0, -1.0), vec2<f32>(3.0, -1.0), vec2<f32>(-1.0, 3.0));
  let c = corners[v];
  var o: VOut;
  o.pos = vec4<f32>(c, 0.0, 1.0);
  o.uv = vec2<f32>(c.x * 0.5 + 0.5, 0.5 - c.y * 0.5);
  return o;
}

fn bilinear(tex: texture_2d<f32>, p: vec2<f32>) -> f32 {
  let q = clamp(p - 0.5, vec2<f32>(0.0), light.size - 1.0);
  let i0 = vec2<i32>(floor(q));
  let i1 = min(i0 + 1, vec2<i32>(light.size) - 1);
  let fr = q - vec2<f32>(i0);
  let a = textureLoad(tex, i0, 0).r;
  let b = textureLoad(tex, vec2<i32>(i1.x, i0.y), 0).r;
  let c = textureLoad(tex, vec2<i32>(i0.x, i1.y), 0).r;
  let d = textureLoad(tex, i1, 0).r;
  return mix(mix(a, b, fr.x), mix(c, d, fr.x), fr.y);
}
// Paper relief alone, and paper plus paint.
fn hPaper(p: vec2<f32>) -> f32 { return bilinear(paperTex, p) * light.paperRelief; }
fn hAll(p: vec2<f32>) -> f32 {
  var paint = 0.0;
  if (light.mode != 2u) { paint = bilinear(elevationTex, p) * light.depth; }
  return hPaper(p) + paint;
}
fn normalAt(p: vec2<f32>, paperOnly: bool) -> vec3<f32> {
  var e: array<f32, 9>;
  var k = 0;
  for (var dy = -1; dy <= 1; dy++) {
    for (var dx = -1; dx <= 1; dx++) {
      let q = p + vec2<f32>(f32(dx), f32(dy));
      e[k] = select(hAll(q), hPaper(q), paperOnly);
      k++;
    }
  }
  let gx = (e[2] + 2.0 * e[5] + e[8] - e[0] - 2.0 * e[3] - e[6]) / 8.0;
  let gy = (e[6] + 2.0 * e[7] + e[8] - e[0] - 2.0 * e[1] - e[2]) / 8.0;
  return normalize(vec3<f32>(-gx, -gy, 1.0));
}
fn toLinear(c: vec3<f32>) -> vec3<f32> {
  return select(pow((c + 0.055) / 1.055, vec3<f32>(2.4)), c / 12.92, c <= vec3<f32>(0.04045));
}
fn toSrgb(c0: vec3<f32>) -> vec3<f32> {
  let c = clamp(c0, vec3<f32>(0.0), vec3<f32>(1.0));
  return select(1.055 * pow(c, vec3<f32>(1.0 / 2.4)) - 0.055, c * 12.92, c <= vec3<f32>(0.0031308));
}

// Lambert + soft height-field shadow + cavity AO + GGX (mirrors Lighting.lightCell).
fn shade(p: vec2<f32>, albedo: vec3<f32>, gloss: f32, paperOnly: bool) -> vec3<f32> {
  let n = normalAt(p, paperOnly);
  let L = light.dir;
  let ndl = max(0.0, dot(n, L));
  let h = select(hAll(p), hPaper(p), paperOnly);
  var shadow = 1.0;
  let horiz = length(L.xy);
  if (horiz > 1e-3 && ndl > 0.0) {
    let dir = L.xy / horiz;
    let rise = L.z / horiz;
    var occ = 0.0;
    for (var s = 1; s <= 12; s++) {
      let t = f32(s) * 1.5;
      let terrain = select(hAll(p + dir * t), hPaper(p + dir * t), paperOnly);
      occ = max(occ, (terrain - (h + rise * t)) / (light.softness * t + 0.05));
    }
    shadow = 1.0 - min(1.0, occ);
  }
  let ring = select(
    (hAll(p + vec2<f32>(-2.0, 0.0)) + hAll(p + vec2<f32>(2.0, 0.0)) + hAll(p + vec2<f32>(0.0, -2.0)) + hAll(p + vec2<f32>(0.0, 2.0))) * 0.25,
    (hPaper(p + vec2<f32>(-2.0, 0.0)) + hPaper(p + vec2<f32>(2.0, 0.0)) + hPaper(p + vec2<f32>(0.0, -2.0)) + hPaper(p + vec2<f32>(0.0, 2.0))) * 0.25,
    paperOnly);
  let ao = clamp(1.0 - (ring - h) * 0.6, 0.4, 1.0);
  let hv = normalize(L + vec3<f32>(0.0, 0.0, 1.0));
  let ndh = max(0.0, dot(n, hv));
  let rough = clamp((1.0 - gloss) * light.roughness, 0.14, 1.0);
  let alpha = rough * rough;
  let a2 = alpha * alpha;
  let denom = ndh * ndh * (a2 - 1.0) + 1.0;
  let D = a2 / (3.14159265 * denom * denom);
  let F = 0.04 + 0.96 * pow(1.0 - max(0.0, hv.z), 5.0);
  let k = alpha * 0.5;
  let ndv = max(1e-3, n.z);
  let G = (ndl / (ndl * (1.0 - k) + k)) * (ndv / (ndv * (1.0 - k) + k));
  var spec = 0.0;
  if (ndl > 0.0) { spec = D * F * G / (4.0 * max(1e-3, ndl) * ndv) * ndl * light.specular * shadow; }
  return albedo * (light.intensity * ndl * shadow * light.colour + light.ambient * ao) + spec * light.colour * light.intensity;
}

@fragment
fn fs(in: VOut) -> @location(0) vec4<f32> {
  let p = in.uv * light.size;
  if (light.mode == 2u) {
    return vec4<f32>(toSrgb(shade(p, light.paperAlbedo, light.paperSheen, true) * light.exposure), 1.0);
  }
  let albedo = toLinear(textureSample(albedoTex, linearSampler, in.uv).rgb);
  let material = textureSample(materialTex, linearSampler, in.uv);
  // Debug views are opaque: their coverage pass is solid.
  if (light.mode == 3u && light.view >= 2u) { return vec4<f32>(1.0, 1.0, 1.0, 1.0); }
  if (light.view == 1u) {
    if (light.mode == 1u || light.mode == 3u) {
      let s = toSrgb(light.substrateAlbedo);
      let c = toSrgb(albedo);
      let a = layerAlpha(c, s, material.b);
      if (light.mode == 3u) { return vec4<f32>(a, a, a, 1.0); }
      return vec4<f32>(clamp(c - s * (1.0 - a), vec3<f32>(0.0), vec3<f32>(a)), a);
    }
    return vec4<f32>(toSrgb(albedo), 1.0);
  }
  if (light.view == 2u) { let v = hAll(p) / max(light.maxElevation * light.depth + light.paperRelief, 1e-4); return vec4<f32>(vec3<f32>(v), 1.0); }
  if (light.view == 3u) { let w = material.g; return vec4<f32>(1.0 - w, 1.0 - 0.6 * w, 1.0, 1.0); }
  if (light.view == 4u) { return vec4<f32>(normalAt(p, false) * 0.5 + 0.5, 1.0); }
  let lit = shade(p, albedo, material.r, false) * light.exposure;
  if (light.mode == 0u) { return vec4<f32>(toSrgb(lit), 1.0); }
  // Layer mode: premultiplied output that reproduces the lit "paint over
  // white substrate" exactly, while neutral glazes act multiplicatively on
  // whatever lies beneath. Alpha is computed from the LIT values (so no
  // channel needs clamping, which would shift hue) and is at least the
  // paint's hiding power.
  let litS = toSrgb(lit);
  let paperS = toSrgb(shade(p, light.substrateAlbedo, light.paperSheen, true) * light.exposure);
  let a = layerAlpha(litS, paperS, material.b);
  if (light.mode == 3u) { return vec4<f32>(a, a, a, 1.0); }
  return vec4<f32>(clamp(litS - paperS * (1.0 - a), vec3<f32>(0.0), vec3<f32>(a)), a);
}

// Smallest alpha with which colour c can be composited over base b (both in
// display space) without any channel going out of range, raised to the
// paint's hiding power.
fn layerAlpha(c: vec3<f32>, b: vec3<f32>, hiding: f32) -> f32 {
  let base = clamp(b, vec3<f32>(1e-3), vec3<f32>(0.999));
  let darken = max(vec3<f32>(0.0), (base - c) / base);
  let lighten = max(vec3<f32>(0.0), (c - base) / (vec3<f32>(1.0) - base));
  let need = max(max(darken.x, darken.y), max(darken.z, max(lighten.x, max(lighten.y, lighten.z))));
  return clamp(max(need, hiding), 0.0, 1.0);
}

// Smallest alpha that represents the change from the white substrate.
fn coverage(albedo: vec3<f32>) -> f32 {
  let s = max(light.substrateAlbedo, vec3<f32>(1e-3));
  let darken = max(vec3<f32>(0.0), (s - albedo) / s);
  let lighten = max(vec3<f32>(0.0), (albedo - s) / max(vec3<f32>(1e-3), vec3<f32>(1.0) - s));
  return clamp(max(max(darken.x, darken.y), max(darken.z, max(lighten.x, max(lighten.y, lighten.z)))), 0.0, 1.0);
}
`;
}
